import { useState, useEffect } from 'react';
import { Col, Container, Form, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import CommonLogo from '../Common/CommonLogo';
import { Btn, H4, P } from '../../../../AbstractElements';
import { Resend } from '../../../../utils/Constant';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthService from '../../../../Api/services/auth.service';
import { useTranslation } from 'react-i18next';

const VerifyCodeContainer = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [forgotPasswordId, setForgotPasswordId] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Obtener datos del estado de navegación o localStorage
    const state = location.state as { email?: string; forgotPasswordId?: number } | null;
    const storedId = localStorage.getItem('forgotPasswordId');
    
    if (state?.forgotPasswordId) {
      setForgotPasswordId(state.forgotPasswordId);
      setEmail(state.email || '');
    } else if (storedId) {
      setForgotPasswordId(parseInt(storedId));
    } else {
      // Si no hay ID, redirigir a forgot password
      toast.error(t("Sesión expirada. Por favor solicita un nuevo código"));
      navigate(`${process.env.PUBLIC_URL}/authentication/forgetpassword`);
    }
  }, [location, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code || code.length < 4) {
      toast.error(t("Por favor ingresa el código completo"));
      return;
    }

    if (!forgotPasswordId) {
      toast.error(t("Error: ID de recuperación no encontrado"));
      return;
    }

    setLoading(true);
    try {
      const response = await AuthService.verifyCode(forgotPasswordId, code);
      
      // Verificar que el status sea "R" (Ready)
      if (response.status === 'R') {
        toast.success(t("Código verificado correctamente"));
        
        // Navegar a la página de cambio de contraseña
        navigate(`${process.env.PUBLIC_URL}/authentication/reset-password`, {
          state: { forgotPasswordId: response.id }
        });
      } else {
        toast.error(t("Código inválido o expirado"));
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        t("Error al verificar el código");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!forgotPasswordId) {
      toast.error(t("Error: ID de recuperación no encontrado"));
      return;
    }

    setResending(true);
    try {
      await AuthService.resendCode(forgotPasswordId);
      toast.success(t("Código reenviado a tu email"));
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        t("Error al reenviar el código");
      toast.error(errorMessage);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="page-wrapper">
      <Container fluid className="p-0">
        <Row>
          <Col xs="12">
            <div className="login-card login-dark">
              <div>
                <div>
                  <CommonLogo />
                </div>
                <div className="login-main">
                  <Form className="theme-form" onSubmit={handleSubmit}>
                    <H4>{t("EnterOTP")}</H4>
                    {email && (
                      <P className="text-muted mb-4">
                        {t("Ingresa el código enviado a")} <strong>{email}</strong>
                      </P>
                    )}
                    <FormGroup>
                      <Label className="col-form-label">{t("Código de Verificación")}</Label>
                      <Input
                        type="text"
                        required
                        placeholder="0000"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        disabled={loading}
                        maxLength={6}
                        className="text-center"
                        style={{ fontSize: '1.5rem', letterSpacing: '0.5rem' }}
                      />
                    </FormGroup>
                    <div className="mt-3 mb-3">
                      <span className="reset-password-link">
                        {t("¿No recibiste el código?")}
                        <Btn
                          color="link"
                          className="btn-link text-danger p-0 ms-1"
                          onClick={handleResendCode}
                          disabled={resending || !forgotPasswordId}
                        >
                          {resending ? (
                            <>
                              <Spinner size="sm" className="me-1" />
                              {t("Reenviando...")}
                            </>
                          ) : (
                            t("Resend")
                          )}
                        </Btn>
                      </span>
                    </div>
                    <FormGroup className="mb-0">
                      <Btn
                        color="primary"
                        block
                        className="w-100"
                        disabled={loading || !code || code.length < 4}
                        type="submit"
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {t("Verificando...")}
                          </>
                        ) : (
                          t("Verificar Código")
                        )}
                      </Btn>
                    </FormGroup>
                    <P className="mt-4 mb-0 text-center">
                      {t("¿Ya tienes una cuenta?")}
                      <Link className="ms-2" to={`${process.env.PUBLIC_URL}/login`}>
                        {t("SignIn")}
                      </Link>
                    </P>
                  </Form>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VerifyCodeContainer;

